<template>
  <div class="quadrilateral-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong><span v-if="mode === 'parallelogram'">AB // CD & AB = CD</span>
        <span v-else-if="mode === 'rectangle'">∠A = 90° | AC = BD</span>
        <span v-else-if="mode === 'rhombus'">AB = BC | AC ⊥ BD</span>
        <span v-else-if="mode === 'trapezoid'">AD // BC & AB ∦ CD</span>
        <span v-else-if="mode === 'isosceles_trapezoid'">AD // BC & AB = CD</span>
        <span v-else>AB = BC & ∠A = 90°</span>$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="280" viewBox="0 0 500 280">
          <g transform="translate(150, 60)">
            <!-- Parallel markers for Trapezoid -->
            <g v-if="mode.includes('trapezoid')">
               <path d="M 50,-10 L 60,0 L 50,10" fill="none" stroke="#ffd700" stroke-width="2" transform="translate(-10, 0)" />
               <path d="M 120,110 L 130,120 L 120,130" fill="none" stroke="#ffd700" stroke-width="2" transform="translate(-10, 0)" />
            </g>

            <!-- Diagonals (Helper lines) -->
            <line :x1="d1.x1" :y1="d1.y1" :x2="d1.x2" :y2="d1.y2" stroke="rgba(255,255,255,0.1)" stroke-dasharray="4" />
            <line :x1="d2.x1" :y1="d2.y1" :x2="d2.x2" :y2="d2.y2" stroke="rgba(255,255,255,0.1)" stroke-dasharray="4" />

            <!-- The Shape -->
            <path :d="shapePath" fill="rgba(0, 210, 255, 0.15)" stroke="#00d2ff" stroke-width="3" />
            
            <!-- Vertices -->
            <circle v-for="(p, i) in points" :key="'v'+i" :cx="p.x" :cy="p.y" r="4" fill="#fff" />
            
            <!-- Right Angle Square for Rectangle/Square -->
            <rect v-if="isRightAngle" :x="points[1].x" :y="points[1].y - 15" width="15" height="15" fill="none" stroke="#ffd700" stroke-width="2" />
            
            <!-- Equal side markers for Rhombus/Square/Isosceles -->
            <g v-if="isEqualSides">
               <line :x1="(points[0].x+points[1].x)/2 - 5" :y1="(points[0].y+points[1].y)/2 - 5" :x2="(points[0].x+points[1].x)/2 + 5" :y2="(points[0].y+points[1].y)/2 + 5" stroke="#ffd700" stroke-width="2" />
               <line :x1="(points[3].x+points[2].x)/2 - 5" :y1="(points[3].y+points[2].y)/2 - 5" :x2="(points[3].x+points[2].x)/2 + 5" :y2="(points[3].y+points[2].y)/2 + 5" stroke="#ffd700" stroke-width="2" v-if="mode === 'parallelogram' || mode === 'rhombus' || mode === 'rectangle' || mode === 'square'" />
               <line :x1="(points[1].x+points[2].x)/2 - 5" :y1="(points[1].y+points[2].y)/2 - 5" :x2="(points[1].x+points[2].x)/2 + 5" :y2="(points[1].y+points[2].y)/2 + 5" stroke="#ffd700" stroke-width="2" />
            </g>
          </g>
        </svg>

        <div class="hud-top">
          <div class="stat">
            <label>当前几何图形: <strong>{{ modeTitle }}</strong></label>
          </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
          <div class="row items-center gap-4">
            <div class="flex-1">
              <label>切换四边形类型</label>
              <el-radio-group v-model="mode" size="mini">
                <el-radio-button label="parallelogram">平行四边形</el-radio-button>
                <el-radio-button label="rectangle">矩形</el-radio-button>
                <el-radio-button label="rhombus">菱形</el-radio-button>
                <el-radio-button label="square">正方形</el-radio-button>
                <el-radio-button label="trapezoid">梯形</el-radio-button>
                <el-radio-button label="isosceles_trapezoid">等腰梯形</el-radio-button>
              </el-radio-group>
            </div>
          </div>
        </div>

        <div class="discovery-note" v-latex>
          <p><strong>{{ modeTitle }}性质：</strong><br>
          <span v-if="mode === 'parallelogram'">两组对边分别平行的四边形。对边相等，对角相等，对角线互相平分。</span>
          <span v-else-if="mode === 'rectangle'">有一个角是直角的平行四边形。四个角都是直角，对角线相等且互相平分。</span>
          <span v-else-if="mode === 'rhombus'">有一组邻边相等的平行四边形。四条边都相等，对角线互相垂直平分，且平分对角。</span>
          <span v-else-if="mode === 'trapezoid'">有一组对边平行，另一组对边不平行的四边形。平行的一组对边叫底，不平行的一组对边叫腰。</span>
          <span v-else-if="mode === 'isosceles_trapezoid'">两腰相等的梯形。在同一底上的两个角相等，对角线相等。</span>
          <span v-else>既是矩形又是菱形的特殊四边形，具备上述所有性质。</span>
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'QuadrilateralViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'parallelogram',
      width: 200,
      height: 120,
      skew: 40
    }
  },
  computed: {
    modeTitle() {
      const map = { 
        parallelogram: '平行四边形', 
        rectangle: '矩形', 
        rhombus: '菱形', 
        square: '正方形',
        trapezoid: '梯形',
        isosceles_trapezoid: '等腰梯形'
      };
      return map[this.mode];
    },
    isRightAngle() { return this.mode === 'rectangle' || this.mode === 'square'; },
    isEqualSides() { return this.mode === 'rhombus' || this.mode === 'square' || this.mode === 'isosceles_trapezoid'; },
    points() {
      let w = this.width;
      let h = this.height;
      let s = this.skew;

      if (this.mode === 'rectangle') { s = 0; }
      if (this.mode === 'rhombus') { 
         s = 60; w = 150; h = 130; 
      }
      if (this.mode === 'square') { s = 0; w = 150; h = 150; }
      
      if (this.mode === 'trapezoid') {
         return [
           { x: 50, y: 0 },
           { x: 150, y: 0 },
           { x: 250, y: h },
           { x: 0, y: h }
         ];
      }
      if (this.mode === 'isosceles_trapezoid') {
         return [
           { x: 50, y: 0 },
           { x: 150, y: 0 },
           { x: 200, y: h },
           { x: 0, y: h }
         ];
      }

      return [
        { x: s, y: 0 },
        { x: s+w, y: 0 },
        { x: w, y: h },
        { x: 0, y: h }
      ];
    },
    shapePath() { return 'M ' + this.points.map(p => `${p.x} ${p.y}`).join(' L ') + ' Z'; },
    d1() { return { x1: this.points[0].x, y1: this.points[0].y, x2: this.points[2].x, y2: this.points[2].y }; },
    d2() { return { x1: this.points[1].x, y1: this.points[1].y, x2: this.points[3].x, y2: this.points[3].y }; }
  },
  mounted() {
    const kp = this.knowledgePoint;
    if (/等腰梯形/.test(kp)) this.mode = 'isosceles_trapezoid';
    else if (/梯形/.test(kp)) this.mode = 'trapezoid';
    else if (/矩形/.test(kp)) this.mode = 'rectangle';
    else if (/菱形/.test(kp)) this.mode = 'rhombus';
    else if (/正方形/.test(kp)) this.mode = 'square';
    else if (/平行/.test(kp)) this.mode = 'parallelogram';
  }
}
</script>

<style scoped lang="scss">
.quadrilateral-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.canvas-box { background: #010205; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.bottom-controls { flex: 0.6; display: flex; flex-direction: column; gap: 10px; overflow-y: auto; }
.control-card { background: rgba(255,255,255,0.02); border-radius: 12px; padding: 15px; }
.discovery-note { background: rgba(0, 210, 255, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
.row { display: flex; gap: 20px; align-items: center; }
label { font-size: 0.75rem; color: #888; margin-bottom: 5px; display: block; }
.flex-1 { flex: 1; }
</style>
